import { Button, ButtonGroup, Card, Tag } from "@blueprintjs/core";
import autobind from "autobind-decorator";
import { Component, default as React } from "react";
import styled from "styled-components";

import HtmlDefault from "common/fragments/HtmlDefault";
import Padding from "common/fragments/Padding";
import { CardView } from "common/views/card/Card.view";
import HeadingView from "common/views/heading/Heading.view";
import { HeadingVariant } from "common/views/heading/Heading.view";
import { FragmentProvider } from "core/components/FragmentProvider";
import { BlockView } from "../../../../common/views/block/BlockView.view";
import { FragmentLayout } from "core/components/FragmentLayout";
import Topic from "../../entity/Topic";
import * as forumActions from "../../forumActions";
import * as actionFragments from './topicActions';
import TopicComponentConfig from "./TopicComponentConfig";
import TopicDisplayMode from "./TopicDisplayMode";
import TopicFormComponent from "./TopicFormComponent";
import TopicListComponent from "./TopicListComponent";
import TopicListConfig from "./TopicListConfig";
import * as metaFragments from './topicMeta';
import ServiceRegistry from 'core/util/ServiceRegistry';
import FetchResolverService from "core/service/resolver/impl/FetchResolver.service";
import { forceQueryRefresh } from "core/actions/paramsActions";
import Bookmark from "panels/forum/entity/Bookmark";
import { IUploadResponse } from "../../../../core/service/upload/IUploadService";
import VerticalCenter from "../../../../common/fragments/VerticalCenter";
import * as IconNames from "@blueprintjs/icons/lib/esm/generated/iconNames";




/**
 * @file displays a forum topic and provides actions for edting it
 *
 * @author jespostio
 */

interface ITopicComponentState {
	edit: boolean;
	topic: Topic;
	reported: boolean;
	replyActive: boolean;
	transientChildren: Topic[];
	bookmark?: Bookmark;
}

export interface ITopicComponentProps {
	topic: Topic;
	config: TopicComponentConfig;
	listConfig: TopicListConfig;
	actions?: any;
	onTopicNavigate?: (topic: Topic, currentDepth: number, reply: boolean) => void;
	mode: TopicDisplayMode;
	reported?: boolean;
	currentDepth?: number;
	reply?: boolean; // show reply form
	tagsEnabled?: boolean;
	writePermision?: boolean;
}

export type ITopicFragmentProps = ITopicComponentProps & ITopicComponentState;

// register the fragments
// export const TopicFragments = FragmentProvider.forFragments<ITopicFragmentProps>("topic:meta", { ...metaFragments, ...actionFragments });

const TopicCont = styled.div`
	display: flex;
	flex-direction: row;
`;

const Children = styled.div`
	margin-left: 7%;
`;

/**
 *
 */
class TopicComponent extends Component<ITopicComponentProps, ITopicComponentState> {

	constructor(props: ITopicComponentProps) {
		super(props);

		// hold the topic in state for editing
		this.state = {
			topic: props.topic,
			edit: false,
			replyActive: props.reply ? true : false,
			reported: false,
			transientChildren: []
		};

	}

	@autobind
	private onTopicEdit(topic: Topic) {
		// optimistically set the topic. the entity form will already have cleared the cache
		// so when the parent component that is provideing data rerenders it should have
		// the updated topic data
		this.setState({ topic, edit: false });
	}

	@autobind
	private onChildCreate(topic: Topic) {
		this.setState({
			transientChildren: [topic, ...this.state.transientChildren],
			replyActive: false
		});
	}

	@autobind
	private getActions() {
		const { currentDepth } = this.props;
		const { topic } = this.state;
		return {
			edit: (editActive) => this.setState({ edit: editActive }),
			navigateTo: (reply?: boolean) => this.props.onTopicNavigate(topic, currentDepth, reply),
			reply: () => this.setState({ replyActive: true }),
			getTopicVote: (topic) => {
				const query = () => `
					{
					  entities: topic(params: {id: `+ topic.id + `}) {
							stat{
								votePositive
								voteNegative
							  }
	  					}
					}
				`;
				const resolverService = ServiceRegistry.get(FetchResolverService);
				return resolverService.execute(query, forceQueryRefresh).then((response) => {
					forceQueryRefresh();
					return response.json();
				}).then((data) => (
					data.data.entities
				)).then((data: any) => {
					this.setState({
						topic: {
							...this.state.topic,
							stats: {
								...this.state.topic.stats,
								votePositive: data.stat.votePositive,
								voteNegative: data.stat.voteNegative
							}
						}
					})
				});
			},
			bookmark:(topic) => {
				const query = () => `
					{
					  entities: topic(params: {id: `+ topic.id + `}) {
							bookmark{
								bookmarked
							  }
	  					}
					}
				`;
				const resolverService = ServiceRegistry.get(FetchResolverService);
				return resolverService.execute(query, forceQueryRefresh).then((response) => {
					()=>this.props.actions.forceQueryRefresh
					return response.json();
				}).then((data) => (
					console.log(data),
					data.data.entities
				)).then((data: any) => {
					this.setState({
						topic: {
							...this.state.topic,
							...this.state.bookmark,
							bookmark:data.bookmark
						}
					})
				});
		},
			...forumActions
		};
	}
	componentDidMount() {
		let topicDisplay = decodeURIComponent(window.location.search.replace(new RegExp("^(?:.*[&\\?]" + encodeURIComponent("topic").replace(/[\.\+\*]/g, "\\$&") + "(?:\\=([^&]*))?)?.*$", "i"), "$1"));;
		let element = document.getElementById(topicDisplay);
		if (element) {
			element.scrollIntoView({
				behavior: "smooth",
				block: "center"
			});
		}
		else {
			document.documentElement.scrollTop = 0;
		}
	}


	public render() {
		const { config, listConfig, mode, currentDepth } = this.props;
		const { topic, edit } = this.state;
		const depthVisible = currentDepth < listConfig.maxDepth;
		const showChildren = TopicDisplayMode.OVERVIEW !== mode && depthVisible;
		const showReply = topic.categoryEntity && depthVisible && this.state.replyActive;
		return (
			<React.Fragment>
				<div id={topic.id.toString()}>
					<CardView viewKey="topic.card" key="parent">
						<TopicCont>
							{edit ? (<TopicFormComponent topic={topic} topicId={topic.id} onAfterSave={this.onTopicEdit} border={false} tagsEnabled={this.props.tagsEnabled}
											config={{...this.props.config}}/>) 
									: (<FragmentLayout
										fragments={{...metaFragments, ...actionFragments }}
										layout={config.topicLayout}
										props={{ ...this.props, ...this.state, actions: this.getActions() }}
										/>)
							}
						</TopicCont>
					</CardView></div>

				<Children>
					{showReply && (
						<React.Fragment>
							<TopicFormComponent
								onAfterSave={this.onChildCreate}
								parentId={topic.id}
								categoryId={topic.categoryEntity.id}
								border={true}
								onClose={() => this.setState({ replyActive: false })}
								tagsEnabled={this.props.tagsEnabled}
								config={{...this.props.config}}
							/>
							<Padding.Base />
						</React.Fragment>
					)}

					{showChildren && (
						<TopicListComponent
							mode={TopicDisplayMode.REPLY}
							params={{ parent: topic.id }}
							topicConfig={{...config, topicLayout: config.topicLayout}}
							listConfig={listConfig}
							transientTopics={this.state.transientChildren}
							currentDepth={currentDepth + 1}
							tagsEnabled={this.props.tagsEnabled}
						/>
					)}
				</Children>
			</React.Fragment>
		);
	}
}

const TopicContent: React.SFC<{ topic: Topic }> = ({ topic }) => (
	<HtmlDefault html={topic.description} />
);

export default TopicComponent;



// WEBPACK FOOTER //
// ./src/panels/forum/component/topic/TopicComponent.tsx